import { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropertyResponse from "@/model/PropertyResponse";
import formatPrice from "@/utils/formatPrice";
import "@/components/PropertiesTable/PropertiesTable.scss";

interface PropertiesTableProps {
  properties: PropertyResponse[];
  /** Extra cell rendered at the end of each row (e.g. a delete button). */
  renderActions?: (property: PropertyResponse) => ReactNode;
}

function PropertiesTable({ properties, renderActions }: PropertiesTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <table className="properties-table">
      <thead>
        <tr>
          <th />
          <th>{t("properties.columns.title")}</th>
          <th>{t("properties.columns.type")}</th>
          <th>{t("properties.columns.location")}</th>
          <th>{t("properties.columns.capacity")}</th>
          <th>{t("properties.columns.nightlyPrice")}</th>
          <th>{t("properties.columns.status")}</th>
          {renderActions && <th />}
        </tr>
      </thead>
      <tbody>
        {properties.map((property, index) => {
          const propertyId = property.id;
          const goToProperty = () => {
            if (propertyId) {
              navigate(`/properties/${propertyId}`);
            }
          };
          const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
            if (event.key === "Enter") {
              goToProperty();
            }
          };
          const stopRowClick = (event: MouseEvent<HTMLTableCellElement>) => {
            event.stopPropagation();
          };
          const cover = property.imageUrls?.[0];

          return (
            <tr
              key={propertyId ?? index}
              className="properties-table__row"
              role={propertyId ? "link" : undefined}
              tabIndex={propertyId ? 0 : undefined}
              onClick={propertyId ? goToProperty : undefined}
              onKeyDown={propertyId ? handleKeyDown : undefined}
            >
              <td>
                {cover ? (
                  <img className="properties-table__thumb" src={cover} alt="" />
                ) : (
                  <span className="properties-table__thumb properties-table__thumb--empty" />
                )}
              </td>
              <td>{property.title ?? "—"}</td>
              <td>
                {property.propertyType
                  ? t(`properties.type.${property.propertyType}`)
                  : "—"}
              </td>
              <td>
                {[property.address?.city, property.address?.country]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </td>
              <td>
                {t("properties.capacitySummary", {
                  guests: property.maxGuests ?? 0,
                  bedrooms: property.bedrooms ?? 0,
                })}
              </td>
              <td>
                {formatPrice(property.nightlyPriceAmount, property.priceCurrency)}
              </td>
              <td>
                {property.status ? (
                  <span
                    className={`status-badge status-badge--${property.status.toLowerCase()}`}
                  >
                    {t(`properties.status.${property.status}`)}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              {renderActions && (
                <td onClick={stopRowClick}>{renderActions(property)}</td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default PropertiesTable;
export type { PropertiesTableProps };
